/**
 * CURRENCY KEEP — STEP 7.2: A READ Fr NUMBER BECOMES ONE CATALOGUE ROW,
 * OR A REFUSAL WITH A NAMED REASON. HIS RULINGS OF 2026-09-22.
 *
 * ── ⚠⚠⚠ WHY THIS EXISTS AT ALL ──────────────────────────────────────
 * The app has identified a note by its Fr number since long before any
 * reading: `main.js`'s `#cfr` handler converts a quick-add note onto a
 * real catalogue row when exactly one row matches what the collector
 * TYPED. Step 7 lets a READING supply that number instead of a finger.
 *
 * That one change moves the act from the collector to the app, and the
 * register entry *THE STAR GLYPH IS THE SLAB LOOKUP'S ONE REAL HAZARD*
 * is why it cannot be done naively:
 *
 *   2,461 catalogue `fr` values carry ★ AND have a non-star twin.
 *   The star row is dearer on 98% of comparable pairs, median 3.00×.
 *   His own three test notes are at 8.3×, 37.5× and 21.3×.
 *
 * ⚠ A reading that silently drops the star produces an EXACT, UNIQUE,
 * SUCCESSFUL match to a real row that is not the collector's note — and
 * systematically the cheaper one. `m.length !== 1` cannot see it:
 * `2055-B` simply is not `2055-B★`, so there is exactly one match and
 * the guard is satisfied. It is not defeated; it was never asked.
 *
 * ── HIS RULINGS, WHICH ARE THE WHOLE OF THE LOGIC BELOW ──────────────
 * 1. The reader reports the Fr EXACTLY as printed, and says plainly when
 *    it cannot tell whether a star is there.  (7.1, `engine-vision.mjs`)
 * 2. When the star state is not clearly legible, REFUSE the match — the
 *    collector picks the note by hand. Missing beats wrong, applied to
 *    identity.
 * 3. ⚠⚠ NARROWED BY HIM THE SAME DAY, with the numbers in front of him:
 *    refuse on an unsure star state ONLY WHEN A ★ TWIN OF THAT Fr EXISTS
 *    in the catalogue. Where no star version exists the star state cannot
 *    change the match, so there is nothing to protect. Measured: 8,246
 *    distinct non-star values, 2,460 (30%) with a ★ twin, 5,786 without.
 * 4. TWO INDEPENDENT SIGNALS — the mark after the Fr on the LABEL, and a
 *    star in the SERIAL NUMBER on the note. A disagreement is "can't
 *    tell".
 *
 * ── ⚠⚠ THE COMBINATION RULE LIVES HERE, NOT IN THE PROMPT ────────────
 * 7.1 asks for `star_label` and `star_serial` separately and combines
 * NOTHING. A rule applied inside a prompt is a rule no gate can drive:
 * it cannot be given two disagreeing signals on purpose and watched to
 * refuse. Here every branch is a function call with an argument.
 *
 * ── ⚠ AND IT WRITES NOTHING ──────────────────────────────────────────
 * Identity is a bigger act than a field, so this module PROPOSES. No
 * `catId` changes in here, no holding is touched, nothing is saved. The
 * conversion that follows a confident match is the one `main.js` already
 * performs and has performed since before any of this — never a second
 * copy of it (his ruling 16's reasoning, applied again).
 */

/** The catalogue's spelling of a star. ⚠ The LABEL prints an asterisk —
 * his correction of 2026-09-22, from his own slabs — and the CATALOGUE
 * stores `★` on all 2,506 rows that carry one, with `*` on none. These
 * are two vocabularies, and translating between them is this module's
 * job. A matcher that forgot it would refuse every star note and look
 * merely cautious while doing it. */
export const CATALOG_STAR = '★'

/** Every glyph a label may print for a star. `*` first because it is
 * what his three slabs actually print; `★` because other labels may. */
export const STAR_GLYPHS = Object.freeze(['*', CATALOG_STAR])

/** The three states a star signal can be in. ⚠ `unsure` is a REAL
 * ANSWER, not a failure to answer — it is the state his ruling 1 added
 * to the reader so that ruling 2 would have something to test. Without
 * it the honest answer and the dropped star are the same bytes. */
export const STAR_STATES = Object.freeze(['yes', 'no', 'unsure'])

/** Why a match was refused. ⚠ EACH ITS OWN REASON, never silence — the
 * shape `suggest.js` already holds for its four refusals, and for the
 * same cause: two states that can both occur must be distinguishable
 * when they do. `no-fr` and `star-unsure` reaching the add screen as one
 * "nothing happened" is exactly what his ruling forbids. */
export const REFUSALS = Object.freeze({
  NO_FR: 'no-fr',
  NO_ROW: 'no-row',
  AMBIGUOUS: 'ambiguous',
  STAR_UNSURE: 'star-unsure',
  /* ── THE SECOND AGREEMENT — HIS RULING OF 2026-09-23, ON THE 7.4 NUMBERS
   * (*"0 right, 1 confidently wrong, 4 refusals on my five slabs is not a
   * lookup"*): before any match, the read DENOMINATION must equal the
   * row's, and the read SERIES, when both are present, must equal the
   * row's. Each disagreement is its own refusal with its own line. */
  DENOM_MISMATCH: 'denomination-mismatch',
  SERIES_MISMATCH: 'series-mismatch',
})

const isStarGlyph = (ch) => STAR_GLYPHS.includes(ch)

/* ══ THE NORMALISER — two vocabularies for one value ══════════════════
 *
 * ⚠⚠ WHY A COMPARISON NEEDS ONE AT ALL. The reader is asked for the
 * denomination *as printed on the label* — `$20`, `10 Cents`, `Nine
 * Pence`, `3 Pound` — and the catalogue stores its own spelling — `$20`,
 * `10¢`, `9d`, `£3`, `60s`, `2s6d`, `$1/2`, `12½¢`, `1,000 Dollars`.
 * Measured 2026-09-23 over the 7.4 run: a literal comparison refuses
 * EVERY RIGHT ROW on the 09-14 set (all 10¢ and 9d readings) while
 * catching nothing extra. So the value is compared, not the spelling.
 *
 * WHAT IT DOES: number words → digits (the scorer's table, his ruling
 * 2026-09-18 — `Nine Pence` and `9 Pence` are one value); vulgar
 * fractions and `a/b` → decimals; then every `<amount><unit>` or
 * `<symbol><amount>` pair is read and summed into ONE value in the
 * currency's base unit — dollars as a decimal (`50 Cents` = `$1/2` =
 * 0.5), sterling in pence (`£3` = `60s` = 720, `2s6d` = 30). The result
 * is `family:value`; a string with no readable amount keeps its own
 * lower-cased text, so `—` equals `—` and nothing else.
 *
 * WHAT IT DOES NOT DO, ON PURPOSE: it does not guess. A string it cannot
 * read as a value is compared as text and will refuse against any row
 * with a real value — the strict reading of *must equal*, RULED strict
 * by him 2026-09-24. The catalogue rows it can never agree with are counted and
 * FROZEN in the gate (`—`, `(written)`, `Weight-denominated`,
 * `Between …`, `… Marbled edge`, `5 oz.`). */
const NUMBER_WORDS = Object.freeze({
  zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
  eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17,
  eighteen: 18, nineteen: 19, twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60, seventy: 70,
  eighty: 80, ninety: 90, hundred: 100, thousand: 1000,
})
const VULGAR = Object.freeze({ '¼': '1/4', '½': '1/2', '¾': '3/4', '⅓': '1/3', '⅔': '2/3', '⅛': '1/8', '⅜': '3/8', '⅝': '5/8', '⅞': '7/8' })
/* unit token → [family, multiplier into the family's base unit] */
const UNITS = Object.freeze({
  '$': ['dollar', 1], dollar: ['dollar', 1], dollars: ['dollar', 1],
  '¢': ['dollar', 0.01], c: ['dollar', 0.01], cent: ['dollar', 0.01], cents: ['dollar', 0.01],
  '£': ['sterling', 240], pound: ['sterling', 240], pounds: ['sterling', 240],
  s: ['sterling', 12], shilling: ['sterling', 12], shillings: ['sterling', 12],
  d: ['sterling', 1], pence: ['sterling', 1], penny: ['sterling', 1], pennies: ['sterling', 1],
})

/** Number words → digits, compounding the usual way (`twenty five` → 25,
 * `one hundred` → 100). Everything else passes through. Also splits the
 * one-word colonial spellings — `sixpence`, `ninepence` — into a number
 * and its unit, because the label prints them that way. */
function wordsToDigits(s) {
  const out = []
  let acc = null
  const flush = () => { if (acc !== null) { out.push(String(acc)); acc = null } }
  for (const raw of s.replace(/-/g, ' ').split(/\s+/)) {
    const pence = /^([a-z]+)pence$/.exec(raw)
    const tok = pence && NUMBER_WORDS[pence[1]] !== undefined ? pence[1] : raw
    const w = NUMBER_WORDS[tok]
    if (w === undefined) { flush(); if (tok !== '') out.push(tok); continue }
    if (w >= 100) acc = (acc ?? 1) * w
    else acc = (acc ?? 0) + w
    if (pence) { flush(); out.push('pence') }
  }
  flush()
  return out.join(' ')
}

/** `12½` → 12.5, `1 1/3` → 1.3333, `1,000` → 1000, `1.25` → 1.25. */
function amountOf(text) {
  const t = text.replace(/,/g, '').trim()
  const m = /^(\d+(?:\.\d+)?)?\s*(?:(\d+)\/(\d+))?$/.exec(t)
  if (!m || (!m[1] && !m[2])) return null
  let v = m[1] ? parseFloat(m[1]) : 0
  if (m[2]) v += parseInt(m[2], 10) / parseInt(m[3], 10)
  return v
}

/**
 * One denomination string → a comparable key.
 * `$20` → `dollar:20` · `10 Cents` and `10¢` → `dollar:0.1` · `Nine
 * Pence` and `9d` → `sterling:9` · `3 Pound`, `£3` and `60s` →
 * `sterling:720` · `2s6d` → `sterling:30` · `—` → `text:—`.
 */
export function normDenomination(s) {
  let t = String(s ?? '').trim().toLowerCase()
  if (!t) return ''
  t = t.replace(/[¼½¾⅓⅔⅛⅜⅝⅞]/g, (ch) => ' ' + VULGAR[ch]).replace(/\s+/g, ' ')
  t = wordsToDigits(t)
  /* `<symbol><amount>` or `<amount><unit>`, where the amount may be a
   * decimal, a mixed number or a fraction. `2s6d` is two pairs. */
  /* ⚠ The bare fraction is tried FIRST: `$1/2` must read as one half, and
   * with the plain number first the regex took `$1` and left `/2` behind
   * (found by the gate's probe, 2026-09-24, before the line was pinned). */
  const AMT = '(\\d+\\/\\d+|\\d[\\d,]*(?:\\.\\d+)?(?:\\s+\\d+\\/\\d+)?)'
  const re = new RegExp('([$£¢])\\s*' + AMT + '|' + AMT + '\\s*([$£¢]|dollars?|cents?|pounds?|shillings?|pence|penny|pennies|[csd])(?![a-z])', 'g')
  const totals = {}
  const seen = new Set()
  let m
  while ((m = re.exec(t)) !== null) {
    const tok = m[1] ?? m[4]
    const unit = UNITS[tok]
    const amount = amountOf(m[2] ?? m[3])
    if (!unit || amount === null) continue
    /* `2s6d` is two units summed; `Between $50–$100` is one unit twice
     * and is NOT a value — it stays text and can never agree. */
    if (seen.has(unit[1])) return 'text:' + t
    seen.add(unit[1])
    totals[unit[0]] = (totals[unit[0]] ?? 0) + amount * unit[1]
  }
  const families = Object.keys(totals)
  if (families.length !== 1) return 'text:' + t
  const v = totals[families[0]]
  return families[0] + ':' + String(Math.round(v * 1e6) / 1e6)
}

/** One series string → a comparable key: `1934A`, `1934-A`, `Series
 * 1934 A` and `1934 a` are one value; `1963B` and `1963-A` are not. */
export function normSeries(s) {
  return String(s ?? '').trim().toUpperCase().replace(/^SERIES\b/, '').replace(/[\s\-–—.]+/g, '')
}

/**
 * THE SECOND AGREEMENT — null when the reading agrees with the row, or
 * the refusal to return. ⚠ It runs on EVERY path that has resolved one
 * row, the literal colonial branch included: a right Fr with a wrong
 * denomination is still a wrong note.
 *
 *   denomination  MUST be read and MUST equal the row's (by value). An
 *                 absent reading cannot equal anything and refuses —
 *                 the strict reading of his *must equal*, and HIS RULING
 *                 2026-09-24: *"Absent read denomination stays strict
 *                 (refuses)."*
 *   series        compared only when BOTH carry one. 5,623 rows carry
 *                 none and the label of a colonial note carries a date
 *                 the catalogue never stores; absence on either side is
 *                 not a disagreement.
 *
 * Each refusal carries what was read and what the row says, so the line
 * can show both — a misread digit is on screen, not silent.
 */
export function disagreement(read, row) {
  const r = read && typeof read === 'object' ? read : {}
  const readDenom = String(r.denomination ?? '').trim()
  const rowDenom = String(row?.denom ?? '').trim()
  if (!readDenom || !rowDenom || normDenomination(readDenom) !== normDenomination(rowDenom)) {
    return { reason: REFUSALS.DENOM_MISMATCH, read: readDenom || null, want: rowDenom || null }
  }
  const readSeries = String(r.series ?? '').trim()
  const rowSeries = String(row?.series ?? '').trim()
  if (readSeries && rowSeries && normSeries(readSeries) !== normSeries(rowSeries)) {
    return { reason: REFUSALS.SERIES_MISMATCH, read: readSeries, want: rowSeries }
  }
  return null
}

/**
 * Strip any trailing star glyph and normalise for comparison.
 * ⚠ Trailing ONLY: a star is printed AFTER the number on a label, and a
 * `*` anywhere else is not a star mark and must not be eaten.
 */
export function baseFr(fr) {
  let s = String(fr ?? '').trim().toUpperCase()
  while (s.length && isStarGlyph(s[s.length - 1])) s = s.slice(0, -1).trim()
  return s
}

/** Does the Fr STRING itself carry a star mark? */
export function frCarriesStar(fr) {
  const s = String(fr ?? '').trim()
  return s.length > 0 && isStarGlyph(s[s.length - 1])
}

/**
 * Combine the signals into one star state — HIS RULING 4, with the
 * ASYMMETRY he ruled on 2026-09-22.
 *
 * ⚠⚠⚠ FIRST ASYMMETRY — THE Fr STRING VOTES ONLY `yes`. A star PRESENT
 * in the string is evidence of a star; a star ABSENT from it is evidence
 * of NOTHING, because a dropped star is the exact failure this module
 * exists to survive. The two fields his ruling named are the ones
 * allowed to vote `no`.
 *
 * ⚠⚠⚠ SECOND ASYMMETRY — HIS RULING, AND IT IS THE SHARPEST THING IN
 * THIS FILE. Where a ★ twin exists (`strictNo`), a `no` must be
 * CONFIRMED BY BOTH SIGNALS. One `no` with the other `unsure` is
 * "can't tell" and refuses.
 *
 * HIS REASON, and it is about which way a mistake falls:
 *   - A single `no` alongside an `unsure` IS THE DROPPED-STAR CASE. It
 *     is what a reader produces when it did not see the star — and it
 *     lands on the CHEAPER row, silently, with a successful-looking
 *     exact match. That is the whole hazard, at a 3.00× median and up
 *     to 37.5× on his own notes.
 *   - A single clear `yes` may still decide, because a FALSE star is
 *     not silent: the note shows `★` on screen and the collector would
 *     notice. A wrong `yes` is visible; a wrong `no` is invisible.
 *
 * ⚠ So the two errors are NOT symmetric and the rule is not either.
 * This is `missing beats wrong` with a second clause: where being wrong
 * is INVISIBLE, require more evidence; where it announces itself, one
 * clear signal is enough.
 *
 * yes+yes → yes · no+no → no · yes+no → unsure (they disagree) ·
 * a lone yes → yes · a lone no → `no` normally, `unsure` under
 * `strictNo` · neither legible → unsure.
 *
 * @param strictNo  true when a ★ twin of this Fr exists in the catalogue
 */
export function starStateFrom(read, { strictNo = false } = {}) {
  const r = read && typeof read === 'object' ? read : {}
  const said = {}
  for (const key of ['star_label', 'star_serial']) {
    const v = String(r[key] ?? '').trim().toLowerCase()
    if (v === 'yes' || v === 'no') said[key] = v
  }
  const votes = Object.values(said)
  /* The string's own mark, which votes `yes` or stays silent. */
  if (frCarriesStar(r.fr_number)) votes.push('yes')

  if (votes.length === 0) return 'unsure'
  /* They disagree — his ruling 4, and it outranks everything below. */
  if (votes.includes('yes') && votes.includes('no')) return 'unsure'
  /* A lone clear `yes` decides: a false star is visible on screen. */
  if (votes.includes('yes')) return 'yes'

  /* Only `no` votes remain. ⚠ Under `strictNo` BOTH of his two named
   * signals must have said it — the Fr string cannot make up the
   * shortfall, because it never votes `no` at all. */
  if (strictNo && !(said.star_label === 'no' && said.star_serial === 'no')) return 'unsure'
  return 'no'
}

/**
 * ONE reading → one catalogue row, or a refusal.
 *
 * @param read     the reader's fields: `fr_number`, `star_label`, `star_serial`,
 *                 and — since his ruling of 2026-09-23 — `denomination`
 *                 and `series`, which must AGREE with the row before it
 *                 is a match
 * @param catalog  the real CATALOG array — rows with an `fr`, a `sid`, a
 *                 `denom` and (on 5,991 of them) a `series`
 * @returns `{ ok: true, row, fr, starState }`
 *       or `{ ok: false, reason, fr, starState, ... }` — a
 *          `denomination-mismatch` / `series-mismatch` refusal also
 *          carries `read` (the label's value), `want` (the row's) and
 *          `row` (the row the Fr resolved to), so the line can say both
 *
 * ⚠ THE CATALOGUE IS AN ARGUMENT, NOT AN IMPORT. It makes the module
 * pure, and it is what lets the gate drive the twin case the real data
 * can never show — the same reasoning `resolveSourceFile` records for
 * refusing on two matching files, and `suggest.js` for its synthetic row.
 */
export function matchFr(read, catalog) {
  const r = read && typeof read === 'object' ? read : {}
  const rows = Array.isArray(catalog) ? catalog : []

  const base = baseFr(r.fr_number)
  if (!base) return { ok: false, reason: REFUSALS.NO_FR, fr: null, starState: starStateFrom(r) }

  /* ⚠ Built over the rows given, every time. A cached index would be a
   * second source of truth about the catalogue, and this project has
   * paid for one of those (`5039` rows, the year→pair cache, H5). */
  const find = (key) => rows.filter((n) => n && n.fr && String(n.fr).trim().toUpperCase() === key)

  /* ── ⚠⚠⚠ THE LITERAL REFERENCE, AND THIS BRANCH WAS PUT HERE BY THE
   * GATE'S FIRST RUN — 2026-09-22.
   *
   * The builder asserted that no catalogue row carries an asterisk. ONE
   * DOES: `COL:VA-145a*`, a Virginia colonial note of May 4 1778, where
   * the `*` is PART OF THE CATALOGUE NUMBER and not a star mark at all.
   * A 1778 colonial note cannot be a star note — star notes are a
   * 20th-century small-size replacement-printing practice. It has no
   * bare twin and no ★ twin: the asterisk is simply how the reference
   * is spelt.
   *
   * ⚠ Without this branch, reading that note correctly would REFUSE it:
   * `baseFr` would eat the asterisk, `VA-145A` matches no row, and the
   * collector would be told to pick by hand on a reading that was right.
   *
   * ⚠⚠ AND IT IS NARROW ON PURPOSE — it fires ONLY when the string
   * CARRIES a mark AND the catalogue holds that exact spelling. The
   * hazard this module exists for is a star that went MISSING, which
   * produces a BARE string; a bare string can never take this path, so
   * no protection is weakened. An exact-match-first rule without the
   * `frCarriesStar` condition would have skipped the star check for
   * every dropped star and undone the entire step.
   *
   * ⚠⚠⚠ AND NARROWED AGAIN BY HIM, 2026-09-22, ON A HOLE HE FOUND IN
   * THE FIRST VERSION: it must fire only when the MATCHED ROW ITSELF is
   * spelt with an asterisk, never when it is spelt with `★`. As first
   * written, a reading of `2055-B★` took the literal path and skipped
   * the star-state check entirely — so `star_label: "no"` with
   * `star_serial: "no"` would have MATCHED THE STAR ROW instead of
   * refusing on the disagreement between the string and both signals.
   * **The branch existed for one colonial spelling and was quietly
   * covering 2,506 star rows as well.** */
  const literal = find(String(r.fr_number ?? '').trim().toUpperCase())
  if (frCarriesStar(r.fr_number) && literal.length === 1 && String(literal[0].fr).includes('*')) {
    const dis = disagreement(r, literal[0])
    if (dis) return { ok: false, ...dis, fr: String(literal[0].fr), starState: 'literal', row: literal[0] }
    return { ok: true, row: literal[0], fr: String(literal[0].fr), starState: 'literal', starTwinExists: false }
  }

  const starKey = base + CATALOG_STAR
  const starTwinExists = find(starKey).length > 0

  /* ⚠⚠⚠ THE TWIN IS LOOKED UP BEFORE THE STAR STATE IS DECIDED, because
   * his ruling makes the EVIDENCE BAR depend on the stakes: where a ★
   * twin exists a lone `no` is not enough. So the catalogue is consulted
   * first and its answer changes how the reading is read. */
  const starState = starStateFrom(r, { strictNo: starTwinExists })

  /* ── HIS RULING 2, NARROWED BY HIS RULING 3 ────────────────────────
   * An unsure star state refuses ONLY where a ★ twin exists. Where none
   * does, there is no second row to land on and nothing to protect.
   *
   * ⚠⚠ THIS IS WHERE THE NARROWING MAKES THE CATALOGUE A SAFETY INPUT.
   * Before it, `unsure` refused on its own; after it, a star row missing
   * from the catalogue turns a refusal into a match. Correct today — the
   * 2,506 star rows are in the data — and a coupling that did not exist
   * before, which is why the gate asserts this branch over REAL rows. */
  if (starState === 'unsure' && starTwinExists) {
    return { ok: false, reason: REFUSALS.STAR_UNSURE, fr: base, starState, starTwinExists: true }
  }

  const key = starState === 'yes' ? starKey : base
  const hits = find(key)

  if (hits.length === 0) return { ok: false, reason: REFUSALS.NO_ROW, fr: key, starState }
  /* ⚠ The shipped `m.length===1` rule, kept exactly: ten catalogue `fr`
   * values are carried by more than one row, and choosing between them
   * is a valuation decision with money in it. */
  if (hits.length > 1) return { ok: false, reason: REFUSALS.AMBIGUOUS, fr: key, starState, count: hits.length }

  /* ── THE SECOND AGREEMENT — HIS RULING, 2026-09-23 ─────────────────
   * The Fr has resolved ONE row. Before it is a match, the label's
   * denomination must be the row's, and its series where both exist.
   * ⚠ This is the rule that would have refused Nonstar 1 on the 7.4
   * run — `1952-D` read for `2055-B`, a $5 row for a $20 label — the
   * confident wrong row the star rules are blind to by construction,
   * because a misread digit produces a clean, unique, successful match.
   * The star rule protects against a DROPPED MARK; this protects against
   * a WRONG NUMBER, and neither can do the other's job. */
  const dis = disagreement(r, hits[0])
  if (dis) return { ok: false, ...dis, fr: key, starState, starTwinExists, row: hits[0] }

  return { ok: true, row: hits[0], fr: key, starState, starTwinExists }
}
